import endpoints from '@app/src/api/endpoints'

import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import { Periods } from '@app/src/components/CreateNewRequestModal/PeriodSelector/PeriodSelector'
import CreationModalStep from '@app/src/components/CreationModal/CreationModalStep'
import { useCreationModalProgress } from '@app/src/context/CreationModalProgressContext'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import RequestError from '@app/src/errors/RequestError'
import useErrorNotification from '@app/src/hooks/errorNotification'
import type { GOAL_TRACKER_QUESTION_KEYS } from '@app/src/pages/Dashboards/GoalTrackerSection/CreateGoalTrackerModal'
import type { COMPANY_PRODUCT_QUESTIONNAIRE_QUESTION_KEYS } from '@app/src/pages/ResourceCollection/Collections/Product/CompanyProductQuestionnaire'
import type { WF_STANDARD_PRODUCT_QUESTIONNAIRE_QUESTION_KEYS } from '@app/src/pages/ResourceCollection/Collections/Product/WfStandardProductQuestionnaire'
import { useFiltersContext } from '@app/src/pages/ResourceCollection/Filters/Filters'
import type { SendRequestResult } from '@app/src/types/resourceExplorer'
import { NotificationSeverity, ResourceTypes } from '@app/src/wf-constants'
import { makeStyles } from '@mui/styles'
import { useQueryClient } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import React, { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import type { COMPANY_QUESTIONNAIRE_QUESTION_KEYS, FormData as FormDataCompany } from './CompanyQuestionnaire'
import type { SFDR_REPORT_QUESTION_KEYS } from './SfdrReport/SfdrReportWithoutCompanies'
import { calculateStartsAndEndsAtDate } from './utils'
import type { FormData as FormDataWf, WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS } from './WfStandardQuestionnaire'

type RequestQuestionStepProps = {
  title: string
  fieldnames: Array<
    | WF_STANDARD_QUESTIONNAIRE_QUESTION_KEYS
    | COMPANY_QUESTIONNAIRE_QUESTION_KEYS
    | SFDR_REPORT_QUESTION_KEYS
    | GOAL_TRACKER_QUESTION_KEYS
    | COMPANY_PRODUCT_QUESTIONNAIRE_QUESTION_KEYS
    | WF_STANDARD_PRODUCT_QUESTIONNAIRE_QUESTION_KEYS
  >
  description?: ReactNode
  hasSkipButton?: boolean
  children: ReactNode
  onClose: () => void
  onBack: () => void
  onSubmit?: () => void
  disableInitialBack?: boolean
  generateInvitationLinkFlow?: boolean
  createGoalTrackerFlow?: boolean
  isLoading?: boolean
  editMode?: boolean
  canGoNext?: (values: unknown) => boolean
  customOnClickNext?: () => Promise<void>
  onSuccess?: () => void
}

const useStyles = makeStyles({
  form: {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
  },
})

export type CombinedFormData = FormDataWf | FormDataCompany

const RequestQuestionStep: React.FC<RequestQuestionStepProps> = ({
  title,
  fieldnames,
  description,
  children,
  hasSkipButton,
  onClose,
  onBack,
  onSubmit,
  disableInitialBack = false,
  generateInvitationLinkFlow = false,
  createGoalTrackerFlow = false,
  isLoading,
  editMode,
  canGoNext,
  customOnClickNext,
  onSuccess,
}) => {
  const { activeStep, totalSteps } = useCreationModalProgress()
  const { mutateAsync: createRequest, isPending: isCreateRequestLoading } = useUpdateResource<
    SendRequestResult,
    unknown
  >()
  const { clearQueryFilter } = useFiltersContext()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()
  const { formatMessage } = useIntl()
  const { handleSubmit } = useFormContext<CombinedFormData>()
  const queryClient = useQueryClient()
  const classes = useStyles()

  const isLastStep = useMemo(() => activeStep === totalSteps, [activeStep, totalSteps])

  const handleCreateRequestResult = async (result: SendRequestResult, values: CombinedFormData) => {
    if (result.duplicateCount > 0 && result.successCount === 0) {
      showSnackbar({
        message: formatMessage({ id: 'form.createRequest.requestsAlreadyExist' }),
        severity: NotificationSeverity.warning,
        disableAutoClose: true,
      })
      return
    }

    const getResultMessage = (result: SendRequestResult) => {
      if (result.isScheduled) {
        return formatMessage({ id: 'form.createRequest.requestBulkCreatedInfo' })
      } else if (result.duplicateCount > 0) {
        return formatMessage(
          { id: 'form.createRequest.requestCreatedWithDuplicates' },
          { duplicateCount: result.duplicateCount },
        )
      } else if (result.successCount > 0) {
        return formatMessage({ id: 'form.createRequest.requestCreatedInfo' })
      }
    }

    showSnackbar({
      message: getResultMessage(result),
      severity: NotificationSeverity.success,
      disableAutoClose: true,
    })

    queryClient.invalidateQueries({ queryKey: [FetchKey.Request] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.InquiryOpenRequests] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.OverdueRequests] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.RequestsOverdueWithinAWeek] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.RequestsOverdueWithinAWeek] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.InquiryByProvider] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.SFDRRequests] })
    queryClient.invalidateQueries({ queryKey: [FetchKey.InquiryStatus] })
    onSuccess?.()
    onClose()
  }

  const handleFormSubmit = async (values: CombinedFormData) => {
    if (onSubmit) {
      onSubmit()
      clearQueryFilter()
      return
    }
    if (!values.templateIds?.length) {
      showErrorNotification({ requestError: new RequestError(400, null, 'Missing template ids') })
      return
    }

    const { periodEndsAt, periodStartsAt } = calculateStartsAndEndsAtDate({
      dateType: values.dateType,
      datePeriod: values.datePeriod,
      dateYear: values.dateYear,
    })

    const body = {
      templateIds: values.templateIds.map(template => template.id),
      ...(values.responderTargetPairs && values.responderTargetPairs.length > 0
        ? { responderTargetPairs: values.responderTargetPairs }
        : { responderProviderIds: [values.responders].flat().map(responder => responder.id) }),
      periodType: Periods.Yearly,
      periodStartsAt,
      periodEndsAt,
      dueAt: values.deadline,
    }

    await createRequest(
      { url: endpoints.saveResourceInBulk(ResourceTypes.Request), body },
      {
        onSuccess: result => handleCreateRequestResult(result, values),
        onError: error => {
          showErrorNotification({ requestError: error })
        },
      },
    )
  }

  const getOverriddenButtonText = () => {
    if (!isLastStep) {
      if (generateInvitationLinkFlow || createGoalTrackerFlow) return 'general.next'
      return undefined
    }

    if (createGoalTrackerFlow) {
      if (editMode) return 'schemas.goalTracker.editGoal'
      return 'schemas.goalTracker.addGoal'
    }

    return generateInvitationLinkFlow ? formatMessage({ id: 'general.done' }) : undefined
  }

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} noValidate className={classes.form}>
      <CreationModalStep
        title={title}
        description={description}
        disableInitialBack={disableInitialBack}
        fieldnames={fieldnames}
        onBack={onBack}
        hasSkipButton={hasSkipButton}
        overrideButtonTextId={getOverriddenButtonText()}
        isLoading={isCreateRequestLoading || isLoading}
        widthOverride={isLastStep && !createGoalTrackerFlow ? 900 : undefined}
        canGoNext={canGoNext}
        customOnClickNext={customOnClickNext}
      >
        {children}
      </CreationModalStep>
    </form>
  )
}

export default RequestQuestionStep
